<template>
  <b-container class="main-container">
    <b-row>
      <b-col>
        <h3>
          {{ site.name }}
          <b-button
            v-if="isSitesManager"
            style="float: right"
            class="ml-2"
            variant="primary"
            :to="{ name: 'siteEdit', params: { siteId: siteId } }"
            >Edit</b-button
          >
          <b-button
            v-if="isSitesManager"
            style="float: right"
            class="ml-2"
            variant="primary"
            v-b-modal.delete-modal
            >Delete</b-button
          >
        </h3>
      </b-col>
    </b-row>

    <b-modal
      id="delete-modal"
      title="Delete Site"
      @ok="deleteSite"
      v-if="site && site.opportunities"
    >
      <b-form class="form-horizontal">
        <p>Are you sure you would like to delete this site?</p>

        <b-alert :show="site.opportunities.length > 0">
          <span
            >All opportunities connected to this site will also be
            deleted.</span
          >
        </b-alert>

        <b-alert :show="dependentContracts.length > 0">
          <p>
            The following contracts will no longer have a site, and will be
            deleted:
          </p>
          <ul>
            <li
              v-for="contract in dependentContracts"
              :key="contract.contractId"
            >
              {{ contract.name }}
            </li>
          </ul>
        </b-alert>

        <b-form-group
          label="Reason"
          label-class="control-label"
          :class="{ 'invalid-group': $v.siteDeletion.deletionReasonId.$error }"
        >
          <b-form-select
            v-model="siteDeletion.deletionReasonId"
            :options="deletionReasons"
            value-field="deletionReasonId"
            text-field="name"
          ></b-form-select>
          <span
            class="span-feedback invalid-feedback"
            v-if="$v.siteDeletion.deletionReasonId.$error"
            >You must select a reason.</span
          >
        </b-form-group>

        <b-form-group label="Notes" label-class="control-label">
          <b-form-textarea
            v-model="siteDeletion.notes"
            placeholder="(optional)"
            rows="3"
            max-rows="6"
          ></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>

    <b-row>
      <b-col sm="9">
        <!-- site details -->
        <widget>
          <site-details :site="site" />
        </widget>

        <!-- site opportunities -->
        <widget heading="Opportunities">
          <site-opportunities
            :opportunities="site.opportunities"
            :site-id="siteId"
          />
        </widget>

        <!-- site visits -->
        <widget heading="Site Visits">
          <site-visits :siteId="siteId" />
        </widget>

        <!-- site contracts -->
        <widget heading="Contracts">
          <site-contracts :contracts="contracts" />
        </widget>

        <!-- site files -->
        <widget heading="Files">
          <site-files :siteId="siteId" />
        </widget>

        <!-- site notes -->
        <!-- <widget heading="Notes">
          <site-notes :siteId="siteId" />
        </widget> -->
      </b-col>
      <b-col sm="3">
        <!-- contacts -->
        <widget heading="Site Contacts">
          <site-contacts
            :contacts="site.siteContacts"
            :siteId="siteId"
            v-if="site.siteContacts"
            @delete="deleteSiteContact"
          />
        </widget>

        <widget heading="Opportunity Totals">
          <site-opportunity-totals :items="siteOpportunities" />
        </widget>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SiteDetails from "../../components/sites/site-details";
import SiteRequests from "../../components/sites/site-requests";
import SiteOpportunities from "../../components/sites/site-opportunities";
import SiteContracts from "../../components/sites/site-contracts";
import StudentActions from "../../components/sites/student-actions";
import SiteFiles from "../../components/sites/site-files";
import SiteNotes from "../../components/sites/site-notes";
import SiteVisits from "../../components/sites/site-visits";
import SiteContacts from "../../components/sites/site-contacts";
import Widget from "../../components/widget";
import SiteOpportunityTotals from "../../components/dashboard/opportunity-semester-summary";
import ShowMore from "../../components/show-more";
import sitesApi from "../../api/sites";
import { mapState } from "vuex";
import toastr from "toastr";
import ToastrSettings from "../../settings/toastr-settings";
import roleChecks from "../../misc/role-checks";
import { required } from "vuelidate/lib/validators";
var htmlEncode = require("js-htmlencode").htmlEncode;
import deletionReasonsApi from "../../api/deletionReasons";
import OpportunityReportApi from "../../api/opportunityReporting";

export default {
  mixins: [roleChecks],
  data() {
    return {
      site: {},
      deletionReasons: [],
      siteDeletion: {
        siteId: null,
        deletionReasonId: null,
        notes: null,
      },
      siteOpportunities: [],
    };
  },

  validations: {
    siteDeletion: {
      deletionReasonId: {
        required,
      },
    },
  },

  props: {
    siteId: {
      required: true,
    },
  },

  components: {
    "site-details": SiteDetails,
    "site-requests": SiteRequests,
    "site-opportunities": SiteOpportunities,
    "site-contracts": SiteContracts,
    "student-actions": StudentActions,
    "site-files": SiteFiles,
    "site-notes": SiteNotes,
    "site-contacts": SiteContacts,
    "site-visits": SiteVisits,
    SiteOpportunityTotals,
    widget: Widget,
    "show-more": ShowMore,
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),

    studentActions: function () {
      if (this.site) {
        return _.map(this.site.siteStudentActions, "studentAction");
      }
      return [];
    },

    contracts: function () {
      if (this.site.contractSites) {
        return _.map(this.site.contractSites, function (contractSite) {
          return contractSite.contract;
        });
      }
      return [];
    },

    // this is any contract that only has one site
    dependentContracts: function () {
      var contractsList = [];
      _.forEach(this.contracts, function (contract) {
        if (contract.contractSites.length == 1) {
          contractsList.push(contract);
        }
      });
      return contractsList;
    },
  },

  methods: {
    deleteSiteContact: function (siteContact) {
      var that = this;
      sitesApi
        .deleteSiteContact(siteContact.siteContactId)
        .then(function () {
          var siteContactIndex = _.findIndex(
            that.site.siteContacts,
            function (contact) {
              return contact.siteContactId == siteContact.siteContactId;
            }
          );
          that.site.siteContacts.splice(siteContactIndex, 1);
          // Toastr Message
          toastr.options = ToastrSettings;
          toastr.success("Contact deleted.", "Success");
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    deleteSite: function (event) {
      var that = this;

      event.preventDefault();
      this.$v.siteDeletion.$touch();

      if (!this.$v.siteDeletion.$invalid) {
        if (this.siteDeletion.notes) {
          this.siteDeletion.notes = htmlEncode(this.siteDeletion.notes);
        }

        sitesApi
          .deleteSite(this.siteDeletion)
          .then(function () {
            toastr.options = ToastrSettings;
            toastr.success("Site deleted.", "Success");
            // navigate back to sites list
            that.$router.push({ name: "sites" });
          })
          .catch(function (error) {
            console.log(error);
          });
      }
    },
  },

  created() {
    var that = this;

    this.siteDeletion.siteId = this.siteId;

    OpportunityReportApi.getStudentOpportunitySiteCounts(this.siteId).then(
      (response) => {
        this.siteOpportunities = response.data;
      }
    );

    sitesApi
      .getSite(this.siteId)
      .then(function (response) {
        that.site = response.data;
      })
      .catch(function (error) {
        console.log(error);
      });

    deletionReasonsApi
      .getDeletionReasons()
      .then(function (response) {
        that.deletionReasons = response.data;
      })
      .catch(function (error) {
        console.log(error);
      });
  },
};
</script>

<style scoped>
.main-container {
  max-width: 100%;
  margin-top: 10px;
}
</style>
